import { Box, BoxProps, HStack, Input, Text, VStack } from '@chakra-ui/react';
import RadioGroupRHF from '@components/RadioGroupRHF';
import { DEFAULT_AMOUNT } from '@config/defaults';
import { defaultTheme } from '@config/theming';
import { useCheckoutConfigContext } from '@contexts/CheckoutConfig';
import { DONATION_FREQUENCY, FormValues } from '@types';
import { FC, useEffect, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Case, Switch, When } from 'react-if';
import AskButton from './AskButton';

const Amount: FC<BoxProps> = (props) => {
  const { t } = useTranslation();
  const { checkout } = useCheckoutConfigContext();
  const { control, watch, getValues } = useFormContext<FormValues>();

  const [amount, setAmount] = useState<string>((watch('amount') || DEFAULT_AMOUNT).toString());

  const { field } = useController({
    name: 'amount',
    control,
    defaultValue: checkout.campaign.default_amount || undefined,
  });

  useEffect(() => {
    const newFreq = getValues('frequency');

    if (newFreq == DONATION_FREQUENCY.ONEOFF) {
      setAmount((checkout.campaign.default_amount || DEFAULT_AMOUNT).toString());
      field.onChange(checkout.campaign.default_amount || DEFAULT_AMOUNT);
    } else {
      setAmount((checkout.campaign.default_subscription_amount || DEFAULT_AMOUNT).toString());
      field.onChange(checkout.campaign.default_subscription_amount || DEFAULT_AMOUNT);
    }
  }, [getValues('frequency')]);

  const frequencyOptions = useMemo(() => {
    const freq: Array<{ label: string; value: DONATION_FREQUENCY }> = [];

    if (checkout.campaign.has_one_off) {
      freq.push({
        label: t('One time'),
        value: DONATION_FREQUENCY.ONEOFF,
      });
    }

    if (checkout.campaign.has_subscription) {
      freq.push({
        label: t('Monthly'),
        value: DONATION_FREQUENCY.MONTHLY,
      });
    }

    return freq;
  }, [checkout]);

  const handleCustomAmountChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    value = value.replace(',', '.');

    if (
      !isNaN(Number(value)) &&
      // Only allow numbers and 2 digits after the dot
      !(value.split('.').length > 1 && value.split('.')[1].length > 2)
    ) {
      // Accept only significant digits
      if (value != '0' && !value.startsWith('0.')) {
        value = value.replace(/^0+/, '');
      }
      setAmount(value);
      field.onChange(parseFloat(value) || 0);
    }
  };

  const handleAmountClick = (amount: number) => {
    setAmount(amount.toString());
    field.onChange(amount);
  };

  return (
    <VStack spacing={5} {...props}>
      <RadioGroupRHF
        name="frequency"
        options={frequencyOptions}
        control={control}
        defaultValue={
          checkout.campaign.has_one_off ? DONATION_FREQUENCY.ONEOFF : DONATION_FREQUENCY.MONTHLY
        }
      />

      <HStack spacing={2} justify="space-evenly" w="full">
        <Switch>
          <Case condition={watch('frequency') == DONATION_FREQUENCY.ONEOFF}>
            <When condition={checkout.campaign.asks.length > 0}>
              {checkout.campaign.asks.map((askAmount) => (
                <AskButton
                  key={askAmount + 'oneoff'}
                  amount={amount}
                  askAmount={askAmount % 1 == 0 ? Math.round(askAmount) : askAmount}
                  onClick={() =>
                    handleAmountClick(askAmount % 1 == 0 ? Math.round(askAmount) : askAmount)
                  }
                  currencySymbol="€"
                />
              ))}
            </When>
          </Case>

          <Case condition={watch('frequency') == DONATION_FREQUENCY.MONTHLY}>
            <When condition={checkout.campaign.asks_subscription.length > 0}>
              {checkout.campaign.asks_subscription.map((askAmount) => (
                <AskButton
                  key={askAmount + 'monthly'}
                  amount={amount}
                  askAmount={askAmount % 1 == 0 ? Math.round(askAmount) : askAmount}
                  onClick={() =>
                    handleAmountClick(askAmount % 1 == 0 ? Math.round(askAmount) : askAmount)
                  }
                  currencySymbol="€"
                />
              ))}
            </When>
          </Case>
        </Switch>
      </HStack>

      <When condition={!checkout.campaign.has_one_off && checkout.campaign.has_subscription}>
        <Text fontSize={20}>{t('Monthly donation')}</Text>
      </When>

      <When
        condition={
          (watch('frequency') == DONATION_FREQUENCY.ONEOFF &&
            checkout.campaign.allow_custom_amount) ||
          checkout.campaign.allow_custom_subscription_amount
        }
      >
        <Box
          borderWidth={1}
          borderColor="blackAlpha.400"
          borderRadius="md"
          backgroundColor="white"
          px={4}
          py={2}
          w="full"
          outline="2px solid transparent"
          _focusWithin={{
            outline: `1px solid ${defaultTheme.colors.primary[500]}}`,
          }}
        >
          <HStack spacing={2} align="baseline">
            <Text>€</Text>
            <Input
              border="none"
              flexGrow={1}
              color="blackAlpha.700"
              fontSize="xl"
              fontWeight="medium"
              placeholder={t('Custom value')}
              textAlign="left"
              value={amount}
              onChange={handleCustomAmountChange}
            />
            <Text>EUR</Text>
          </HStack>
        </Box>
      </When>
    </VStack>
  );
};

export default Amount;
